import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import { Tabs } from '../components/ui/Tabs';
import { PageHeader } from '../components/ui/PageHeader';
import { useAppSelector } from '../hooks/redux';
import SupportTicketModal from '../components/SupportTicketModal';
import { Button } from '../components/ui/Button';

interface Attachment {
  filename: string;
  originalName: string;
  s3Key: string;
  s3Url: string;
  cloudFrontUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

interface SupportTicket {
  id: string;
  firebaseUid: string;
  name: string;
  email: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  attachments: Attachment[];
  adminNotes?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const SupportTickets: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<'all' | 'my-tickets'>('all');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');

  // Pagination
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 15,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filterStatus, filterSeverity, viewMode]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      if (filterSeverity !== 'all') {
        params.append('severity', filterSeverity);
      }

      // Choose endpoint based on view mode
      const endpoint = viewMode === 'my-tickets'
        ? `/admin/support-tickets/my-tickets?${params}`
        : `/admin/support-tickets?${params}`;

      const response = await apiService.get(endpoint);

      if (response?.success) {
        setTickets(response.data.tickets || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: 15,
          total: 0,
          pages: 0
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch support tickets:', err);
      setError(err.message || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = async (data: { title: string; description: string; severity: 'low' | 'medium' | 'high'; files: File[]; filesToDelete?: string[] }) => {
    try {
      setSubmitting(true);
      setError(null);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('severity', data.severity);

      // Add files
      data.files.forEach(file => {
        formData.append('attachments', file);
      });

      // Add files to delete (if any)
      if (data.filesToDelete && data.filesToDelete.length > 0) {
        formData.append('filesToDelete', JSON.stringify(data.filesToDelete));
      }

      let response;
      if (editingTicket) {
        // Update existing ticket
        response = await apiService.putFormData(`/admin/support-tickets/my-tickets/${editingTicket.id}`, formData);
        setSuccess('Support ticket updated successfully');
      } else {
        // Create new ticket
        response = await apiService.postFormData('/admin/support-tickets', formData);
        setSuccess('Support ticket created successfully');
      }

      if (response?.success) {
        setShowModal(false);
        setEditingTicket(null);
        fetchTickets();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      console.error('Failed to submit support ticket:', err);
      setError(err.message || 'Failed to submit support ticket');
      throw err; // Re-throw to let modal handle it
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTicket = (ticket: SupportTicket) => {
    // Only allow users to edit their own tickets
    if (user && ticket.firebaseUid !== user.uid) {
      setError('You can only edit your own support tickets');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setEditingTicket(ticket);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTicket(null);
    setError(null);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-error-tint text-error';
      case 'medium':
        return 'bg-warning-tint text-warning';
      case 'low':
        return 'bg-success-tint text-success';
      default:
        return 'bg-surface-sunken text-ink-secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-info-tint text-info';
      case 'in_progress':
        return 'bg-warning-tint text-warning';
      case 'resolved':
        return 'bg-success-tint text-success';
      case 'closed':
        return 'bg-surface-sunken text-ink-secondary';
      default:
        return 'bg-surface-sunken text-ink-secondary';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const tabLabel = (mode: typeof viewMode, text: string) => (
    <span className="inline-flex items-center gap-2">
      {text}
      {viewMode === mode && loading && (
        <span data-testid={`tickets-loading-${mode}`} className="inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-signature" />
      )}
    </span>
  );

  return (
    <MainLayout title="Support Tickets">
      <div className="p-2">
        <PageHeader
          title="Support Tickets"
          description="Manage and track support requests"
          actions={(
            <Button variant="primary" onClick={() => setShowModal(true)}>
              Create ticket
            </Button>
          )}
        />

        {success && (
          <div className="mb-6 bg-success-tint border border-success text-ink px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="mb-6 bg-error-tint border border-error text-error px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* The active tab shows a spinner while its list loads, then its count. */}
        <Tabs
          idPrefix="tickets"
          ariaLabel="Ticket views"
          value={viewMode}
          onChange={(id) => {
            setViewMode(id as typeof viewMode);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          items={[
            { id: 'all', label: tabLabel('all', 'All Tickets'), disabled: loading, count: viewMode === 'all' && !loading ? pagination.total : undefined },
            { id: 'my-tickets', label: tabLabel('my-tickets', 'My Tickets'), disabled: loading, count: viewMode === 'my-tickets' && !loading ? pagination.total : undefined },
          ]}
        />

        {/* Filters */}
        <div className="bg-surface rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">Status</label>
              <select
                className="px-4 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">Severity</label>
              <select
                className="px-4 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature"
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
              >
                <option value="all">All Severity</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <Button
              variant="primary"
              onClick={fetchTickets}
              className="self-end"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Tickets Table */}
        <div className="bg-surface rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-signature mx-auto"></div>
              <p className="mt-2 text-ink-muted">Loading tickets...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-hairline">
                  <thead className="bg-surface-subtle">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        Ticket Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        Attachments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-hairline">
                    {tickets.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-ink-muted">
                          No support tickets found
                        </td>
                      </tr>
                    ) : (
                      tickets.map((ticket) => {
                        const isOwnTicket = user && ticket.firebaseUid === user.uid;
                        return (
                        <tr
                          key={ticket.id}
                          onClick={() => handleEditTicket(ticket)}
                          className={`transition-colors ${isOwnTicket ? 'cursor-pointer hover:bg-signature-tint' : 'cursor-default opacity-75'}`}
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-ink">
                              {ticket.title}
                            </div>
                            <div className="text-xs text-ink-muted mt-1 line-clamp-2">
                              {ticket.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-ink">{ticket.name}</div>
                            <div className="text-xs text-ink-muted">{ticket.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(ticket.severity)}`}>
                              {ticket.severity.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {ticket.attachments && ticket.attachments.length > 0 ? (
                              <div className="space-y-1">
                                {ticket.attachments.map((attachment, index) => (
                                  <a
                                    key={index}
                                    href={attachment.cloudFrontUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center text-xs text-signature hover:text-signature-strong"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    {attachment.originalName}
                                    <span className="ml-1 text-ink-muted">
                                      ({formatFileSize(attachment.fileSize)})
                                    </span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-ink-muted">No attachments</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-ink-muted">
                            {format(new Date(ticket.createdAt), 'MMM dd, yyyy HH:mm')}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="px-6 py-4 bg-surface-subtle border-t border-hairline">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-ink-secondary">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} results
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.pages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Support Ticket Modal */}
        <SupportTicketModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSubmit={handleModalSubmit}
          ticket={editingTicket}
          submitting={submitting}
          error={error}
        />
      </div>
    </MainLayout>
  );
};

export default SupportTickets;
